import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "./AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import { ThemeContext } from "../context/ThemeContext";

const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

function bytesOfBase64DataUrl(dataUrl) {
  const comma = dataUrl.indexOf(",");
  if (comma === -1) return 0;
  const b64 = dataUrl.slice(comma + 1);
  return Math.floor((b64.length * 3) / 4);
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function isImageFile(file) {
  return file && typeof file.type === "string" && file.type.startsWith("image/");
}

function detectIsDark() {
  try {
    const root = document.documentElement;
    const attr = (root.getAttribute("data-theme") || "").toLowerCase();
    if (attr.includes("dark")) return true;
    if (attr.includes("light")) return false;

    const cls = root.className || "";
    if (/\bdark\b/i.test(cls)) return true;
    if (/\blight\b/i.test(cls)) return false;

    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return true;
  }
}

export default function GlobalFeedbackButton() {
  const { authorizedFetch, user } = useContext(AuthContext);
  const { language, setLanguage, t } = useContext(LanguageContext);
  const { theme, setTheme } = useContext(ThemeContext);

  const VISITOR_KEY = "mt_visitor_id";
  const BG_GRID_KEY = "global.backgroundGrid.enabled";
  const BG_VARIANT_KEY = "global.background.variant";
  const BG_VARIANTS = ["default", "navy", "green"];

  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [type, setType] = useState("BUG");
  const [message, setMessage] = useState("");
  const [images, setImages] = useState([]);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [bgGridEnabled, setBgGridEnabled] = useState(() => {
    try {
      const raw = window.localStorage.getItem(BG_GRID_KEY);
      if (raw === "0") return false;
      if (raw === "1") return true;
    } catch {}
    return true;
  });
  const [bgVariant, setBgVariant] = useState(() => {
    try {
      const raw = String(window.localStorage.getItem(BG_VARIANT_KEY) || "default").toLowerCase();
      return BG_VARIANTS.includes(raw) ? raw : "default";
    } catch {}
    return "default";
  });

  const [isDark, setIsDark] = useState(() => detectIsDark());

  const modalRef = useRef(null);
  const dropRef = useRef(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const root = document.documentElement;
    const update = () => setIsDark(detectIsDark());

    update();

    const mo = new MutationObserver(update);
    mo.observe(root, { attributes: true, attributeFilter: ["class", "data-theme"] });

    let mql;
    if (window.matchMedia) {
      mql = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => update();
      if (mql.addEventListener) mql.addEventListener("change", onChange);
      else mql.addListener(onChange);
      return () => {
        mo.disconnect();
        if (mql.removeEventListener) mql.removeEventListener("change", onChange);
        else mql.removeListener(onChange);
      };
    }

    return () => mo.disconnect();
  }, []);

  const pageUrl = useMemo(() => {
    try {
      return window.location.href;
    } catch {
      return "";
    }
  }, [open]);

  const meta = useMemo(() => {
    try {
      return {
        pathname: window.location.pathname,
        search: window.location.search,
        userAgent: navigator.userAgent,
        screen: { w: window.screen?.width, h: window.screen?.height },
        viewport: { w: window.innerWidth, h: window.innerHeight },
        theme: isDark ? "dark" : "light",
      };
    } catch {
      return { theme: isDark ? "dark" : "light" };
    }
  }, [open, isDark]);

  useEffect(() => {
    if (!open && !settingsOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setSettingsOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, settingsOpen]);

  useEffect(() => {
    const openFeedback = (event) => {
      if (typeof event?.detail?.message === "string") {
        setMessage(event.detail.message); setType("BUG");
      }
      setOpen(true);
      setErr("");
      setOk(false);
    };
    const openSettings = () => setSettingsOpen(true);

    window.addEventListener("matematron:open-feedback", openFeedback);
    window.addEventListener("matematron:open-settings", openSettings);
    return () => {
      window.removeEventListener("matematron:open-feedback", openFeedback);
      window.removeEventListener("matematron:open-settings", openSettings);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPaste = async (e) => {
      try {
        const items = e.clipboardData?.items || [];
        const fileItems = Array.from(items).filter((it) => it.kind === "file");
        if (!fileItems.length) return;

        for (const it of fileItems) {
          const f = it.getAsFile?.();
          if (!f || !isImageFile(f)) continue;
          await addImageFile(f);
        }
      } catch (ex) {
        console.error(ex);
      }
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open, images]);

  const addImageFile = async (file) => {
    setErr("");
    setOk(false);

    if (!isImageFile(file)) {
      setErr(t("feedback.errorsOnlyImages"));
      return;
    }
    if (images.length >= MAX_IMAGES) {
      setErr(t("feedback.errorsMaxImages", { count: MAX_IMAGES }));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setErr(t("feedback.errorsImageTooBig"));
      return;
    }

    const dataUrl = await fileToDataUrl(file);
    const bytes = bytesOfBase64DataUrl(dataUrl);
    if (bytes > MAX_IMAGE_BYTES) {
      setErr(t("feedback.errorsImageTooBigConverted"));
      return;
    }

    setImages((prev) => [
      ...prev,
      {
        name: file.name || "screenshot.png",
        mime: file.type || "image/png",
        dataUrl,
        bytes,
      },
    ]);
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await addImageFile(f);
    e.target.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    for (const f of files) await addImageFile(f);
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    try {
      window.localStorage.setItem(BG_GRID_KEY, bgGridEnabled ? "1" : "0");
    } catch {}
  }, [bgGridEnabled]);

  useEffect(() => {
    try {
      window.localStorage.setItem(BG_VARIANT_KEY, bgVariant);
    } catch {}
  }, [bgVariant]);

  useEffect(() => {
    const body = document.body;
    if (!body) return;
    body.setAttribute("data-bg-grid", bgGridEnabled ? "on" : "off");
    body.setAttribute("data-bg-variant", bgVariant);
  }, [bgGridEnabled, bgVariant]);

  const resetForm = () => {
    setType("BUG");
    setMessage("");
    setImages([]);
    setErr("");
    setOk(false);
  };

  const isThemeDark = theme === "dark";
  const toggleTheme = () => setTheme((current) => (current === "dark" ? "light" : "dark"));

  const submit = async () => {
    setErr("");
    setOk(false);

    const msg = message.trim();
    if (msg.length < 3) {
      setErr(t("feedback.errorsMinMessage"));
      return;
    }

    setSending(true);
    try {
      let visitorId = "";
      try {
        visitorId = String(window.localStorage.getItem(VISITOR_KEY) || "").trim();
        if (!visitorId) {
          visitorId =
            window.crypto && typeof window.crypto.randomUUID === "function"
              ? window.crypto.randomUUID()
              : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
          window.localStorage.setItem(VISITOR_KEY, visitorId);
        }
      } catch {}

      const payload = {
        type,
        message: msg,
        pageUrl,
        meta,
        visitorId,
        attachments: images.map((x) => ({
          name: x.name,
          mime: x.mime,
          dataUrl: x.dataUrl,
        })),
      };

      const r = await authorizedFetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);

      setOk(true);
      resetForm();
      setTimeout(() => setOpen(false), 900);
    } catch (e) {
      setErr(e?.message || t("feedback.errorsSendFailed"));
    } finally {
      setSending(false);
    }
  };

  const themeVars = isDark
    ? `
      --fb-accent: rgba(255,165,0,1);
      --fb-border: rgba(255,165,0,0.55);
      --fb-text: rgba(255,255,255,0.92);
      --fb-muted: rgba(255,255,255,0.72);
      --fb-surface: rgba(10,16,32,0.88);
      --fb-surface-2: rgba(255,255,255,0.04);
      --fb-overlay: rgba(0,0,0,0.55);
      --fb-shadow: 0 20px 60px rgba(0,0,0,0.55);
      --fb-input-bg: rgba(0,0,0,0.00);
      --fb-input-border: rgba(255,165,0,0.55);
      color-scheme: dark;
    `
    : `
      --fb-accent: rgba(255,140,0,1);
      --fb-border: rgba(255,140,0,0.55);
      --fb-text: rgba(15,23,42,0.96);
      --fb-muted: rgba(15,23,42,0.72);
      --fb-surface: rgba(255,255,255,0.94);
      --fb-surface-2: rgba(15,23,42,0.04);
      --fb-overlay: rgba(15,23,42,0.20);
      --fb-shadow: 0 20px 60px rgba(15,23,42,0.18);
      --fb-input-bg: rgba(255,255,255,0.80);
      --fb-input-border: rgba(255,140,0,0.55);
      color-scheme: light;
    `;

  const btnTextColor = "rgba(10,16,32,0.98)";

  const btnBottom = "calc(env(safe-area-inset-bottom, 0px) + 84px)";

  return (
    <>
      <style>{`
        .fb-scope{ ${themeVars} }

        .fb-stack{
          position: fixed;
          right: 18px;
          bottom: ${btnBottom};
          z-index: 9999;
          display: grid;
          gap: 10px;
          width: 52px;
          justify-items: center;
        }

        .fb-btn{
          width: 52px;
          height: 52px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          border: 1px solid rgba(255,165,0,0.95);
          background: rgba(255,165,0,0.95);
          color: ${btnTextColor};
          font-size: 24px;
          line-height: 1;
          font-weight: 1000;
          cursor: pointer;
          box-shadow: none;
        }
        .fb-btn:hover{ filter: brightness(1.04); }
        .fb-btn:active{ transform: translateY(1px) scale(0.99); }
        .fb-gear-btn{
          width: 52px;
          height: 52px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          border: 1px solid rgba(255,165,0,0.95);
          background: rgba(255,165,0,0.95);
          color: rgba(10,16,32,0.98);
          font-size: 24px;
          font-weight: 900;
          line-height: 1;
          cursor: pointer;
          box-shadow: none;
        }
        .fb-gear-btn:hover{ filter: brightness(1.04); }
        .fb-gear-btn:active{ transform: translateY(1px) scale(0.99); }
        .fb-up-btn{
          width: 40px;
          height: 40px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          border: 1px solid var(--fb-border);
          background: var(--fb-surface);
          color: var(--fb-text);
          font-size: 18px;
          font-weight: 900;
          line-height: 1;
          cursor: pointer;
          box-shadow: none;
        }
        .fb-up-btn:hover{ border-color: var(--fb-accent); }
        .fb-up-btn:active{ transform: translateY(1px) scale(0.99); }
        body.inline-whiteboard-fullscreen-active .fb-stack{ display: none !important; }
        body.whiteboard-room-fullscreen-active .fb-stack{ display: none !important; }

        .fb-overlay{
          position: fixed;
          inset: 0;
          z-index: 10000;
          background: var(--fb-overlay);
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 18px;
        }

        .fb-modal{
          width: min(760px, 96vw);
          max-height: 86vh;
          overflow:auto;
          border-radius: 16px;
          border: 1px solid var(--fb-border);
          background: var(--fb-surface);
          box-shadow: var(--fb-shadow);
          color: var(--fb-text);
          padding: 14px;
        }

        .fb-row{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; }
        .fb-title{ font-size: 18px; font-weight: 1000; color: var(--fb-text); }

        .fb-x{
          margin-left:auto;
          border:1px solid var(--fb-border);
          background: transparent;
          color: var(--fb-text);
          border-radius: 10px;
          padding: 6px 10px;
          cursor:pointer;
          font-weight: 900;
        }
        .fb-x:hover{ border-color: var(--fb-accent); }

        .fb-select,.fb-text,.fb-btn2,.fb-mini{
          border: 1px solid var(--fb-input-border);
          background: var(--fb-input-bg);
          color: var(--fb-text);
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 800;
        }

        .fb-select{ color-scheme: inherit; }
        .fb-select option{
          background: ${isDark ? "rgb(10,16,32)" : "rgb(255,255,255)"};
          color: ${isDark ? "rgba(255,255,255,0.92)" : "rgba(15,23,42,0.96)"};
        }

        .fb-btn2{ cursor:pointer; padding: 10px 14px; background: transparent; }
        .fb-btn2:hover{ border-color: var(--fb-accent); }

        .fb-text{ width:100%; min-height: 120px; resize: vertical; font-weight: 700; line-height: 1.35; }

        .fb-help{ opacity: .9; font-size: 12px; line-height: 1.35; color: var(--fb-muted); }
        .fb-settings-grid{ display: grid; gap: 14px; margin-top: 8px; }
        .fb-settings-row{
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          border: 1px solid var(--fb-input-border);
          border-radius: 12px;
          padding: 10px 12px;
          background: var(--fb-surface-2);
        }
        .fb-toggle{ display: inline-flex; align-items: center; gap: 8px; font-weight: 800; color: var(--fb-text); }
        .fb-settings-select{ min-width: 220px; }

        .fb-drop{ border: 1px dashed var(--fb-border); border-radius: 14px; padding: 12px; background: var(--fb-surface-2); }

        .fb-thumbs{ display:flex; gap:10px; flex-wrap:wrap; margin-top: 10px; }
        .fb-thumb{ width: 140px; border-radius: 12px; border: 1px solid rgba(0,0,0,0.10); overflow:hidden; background: rgba(255,255,255,0.02); }
        .fb-thumb img{ width:100%; height: 90px; object-fit: cover; display:block; }
        .fb-thumb .meta{ padding: 8px; font-size: 12px; opacity:.92; color: var(--fb-text); }

        .fb-mini{ cursor:pointer; padding: 6px 10px; border-radius: 10px; background: transparent; }
        .fb-mini:hover{ border-color: var(--fb-accent); }

        .fb-actions{ display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap; margin-top: 12px; }

        .fb-ok{ font-weight: 1000; color: rgba(16,185,129,1); }
        .fb-err{ font-weight: 1000; color: var(--fb-accent); }
      `}</style>

      <div className="fb-scope">
        {settingsOpen && (
          <div
            className="fb-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setSettingsOpen(false);
            }}
          >
            <div className="fb-modal">
              <div className="fb-row">
                <div className="fb-title">{t("feedback.settingsBackgroundTitle")}</div>
                <button className="fb-x" onClick={() => setSettingsOpen(false)}>
                  {t("common.close")}
                </button>
              </div>

              <div className="fb-settings-grid">
                <div className="fb-settings-row">
                  <div style={{ fontWeight: 900, color: "var(--fb-text)" }}>{t("feedback.settingsGrid")}</div>
                  <label className="fb-toggle">
                    <input
                      type="checkbox"
                      checked={bgGridEnabled}
                      onChange={(e) => setBgGridEnabled(Boolean(e.target.checked))}
                    />
                    <span>{bgGridEnabled ? t("common.enabled") : t("common.disabled")}</span>
                  </label>
                </div>

                <div className="fb-settings-row">
                  <div style={{ fontWeight: 900, color: "var(--fb-text)" }}>{t("feedback.settingsVariant")}</div>
                  <select
                    className="fb-select fb-settings-select"
                    value={bgVariant}
                    onChange={(e) => setBgVariant(String(e.target.value || "default"))}
                  >
                    <option value="default">{t("feedback.settingsVariantDefault")}</option>
                    <option value="navy">{t("feedback.settingsVariantNavy")}</option>
                    <option value="green">{t("feedback.settingsVariantGreen")}</option>
                  </select>
                </div>

                <div className="fb-settings-row">
                  <div style={{ fontWeight: 900, color: "var(--fb-text)" }}>{t("feedback.settingsTheme")}</div>
                  <button type="button" className="fb-btn2" onClick={toggleTheme}>
                    {isThemeDark ? t("feedback.settingsThemeDark") : t("feedback.settingsThemeLight")}
                  </button>
                </div>

                {isAdmin && (
                  <div className="fb-settings-row">
                    <div style={{ fontWeight: 900, color: "var(--fb-text)" }}>{t("feedback.settingsLanguage")}</div>
                    <select
                      className="fb-select fb-settings-select"
                      value={language}
                      onChange={(e) => setLanguage(String(e.target.value || "pl"))}
                    >
                      <option value="pl">{t("languages.pl")}</option>
                      <option value="en">{t("languages.en")}</option>
                    </select>
                  </div>
                )}

                <div className="fb-help">{t("feedback.settingsHelp")}</div>
              </div>
            </div>
          </div>
        )}

        {open && (
          <div
            className="fb-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <div className="fb-modal" ref={modalRef}>
              <div className="fb-row">
                <div className="fb-title">{t("feedback.reportTitle")}</div>
                <button className="fb-x" onClick={() => setOpen(false)}>
                  {t("common.close")}
                </button>
              </div>

              <div style={{ height: 10 }} />

              <div className="fb-row">
                <select className="fb-select" value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="BUG">{t("feedback.typeBug")}</option>
                  <option value="SUGGESTION">{t("feedback.typeSuggestion")}</option>
                  <option value="OTHER">{t("feedback.typeOther")}</option>
                </select>
              </div>

              <div style={{ height: 10 }} />

              <textarea
                className="fb-text"
                placeholder={t("feedback.messagePlaceholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div style={{ height: 10 }} />

              <div className="fb-drop" ref={dropRef} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
                <div className="fb-row" style={{ justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 1000, color: "var(--fb-text)" }}>{t("feedback.screenshotLabel")}</div>
                  <label className="fb-mini">
                    {t("feedback.addFile")}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      onChange={onPickFiles}
                    />
                  </label>
                </div>

                <div className="fb-help" style={{ marginTop: 6 }}>
                  {t("feedback.screenshotHelp1")}
                  <br />
                  {t("feedback.screenshotHelp2")}
                  <br />
                  {t("feedback.screenshotHelp3", { count: MAX_IMAGES })}
                </div>

                {images.length > 0 && (
                  <div className="fb-thumbs">
                    {images.map((x, idx) => (
                      <div className="fb-thumb" key={idx}>
                        <img src={x.dataUrl} alt="" />
                        <div className="meta">
                          <div style={{ fontWeight: 900 }}>{x.name}</div>
                          <div style={{ opacity: 0.85 }}>{(x.bytes / 1024).toFixed(0)} KB</div>
                          <div style={{ marginTop: 6 }}>
                            <button className="fb-mini" onClick={() => removeImage(idx)}>
                              {t("feedback.remove")}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ height: 10 }} />

              {err && <div className="fb-err">{t("feedback.errorPrefix")} {err}</div>}
              {ok && <div className="fb-ok">{t("feedback.sent")}</div>}

              <div className="fb-actions">
                <button className="fb-mini" onClick={resetForm} disabled={sending}>
                  {t("feedback.clear")}
                </button>
                <button className="fb-btn2" onClick={submit} disabled={sending}>
                  {sending ? t("feedback.sending") : t("feedback.send")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
